import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle, Shield, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

type RiskLevel = 'unacceptable' | 'high' | 'limited' | 'minimal';

interface AISystem {
  id: string;
  name: string;
  purpose: string;
  useCase: string;
  riskLevel: RiskLevel;
  assessmentDate: string;
  complianceStatus: 'compliant' | 'non-compliant' | 'under-review';
  measures: string;
}

const riskLevelInfo = {
  unacceptable: {
    label: 'Unannehmbares Risiko',
    color: 'destructive',
    icon: XCircle,
    description: 'Verboten nach EU AI Act',
    examples: 'Social Scoring, biometrische Echtzeit-Identifikation im öffentlichen Raum, manipulative KI'
  },
  high: {
    label: 'Hohes Risiko',
    color: 'destructive',
    icon: AlertTriangle,
    description: 'Strenge Konformitätspflichten',
    examples: 'Medizinische Diagnostik, kritische Infrastruktur, Einstellungsentscheidungen, Kreditvergabe'
  },
  limited: {
    label: 'Begrenztes Risiko',
    color: 'default',
    icon: Shield,
    description: 'Transparenzpflichten',
    examples: 'Chatbots, Deepfakes, Emotionserkennung'
  },
  minimal: {
    label: 'Minimales Risiko',
    color: 'default',
    icon: CheckCircle,
    description: 'Keine besonderen Anforderungen',
    examples: 'Spamfilter, Videospiele, einfache Empfehlungssysteme'
  }
};

export const AIActRiskAssessment = () => {
  const [systems, setSystems] = useState<AISystem[]>(() => {
    const saved = localStorage.getItem('ai-systems');
    return saved ? JSON.parse(saved) : [];
  });

  const [newSystem, setNewSystem] = useState({
    name: '',
    purpose: '',
    useCase: '',
    riskLevel: 'minimal' as RiskLevel,
    measures: ''
  });

  const addSystem = () => {
    if (!newSystem.name || !newSystem.purpose) {
      toast.error('Name und Zweck sind erforderlich');
      return;
    }

    const system: AISystem = {
      id: crypto.randomUUID(),
      ...newSystem,
      assessmentDate: new Date().toISOString().split('T')[0],
      complianceStatus: 'under-review'
    };

    const updated = [...systems, system];
    setSystems(updated);
    localStorage.setItem('ai-systems', JSON.stringify(updated));
    
    setNewSystem({
      name: '',
      purpose: '',
      useCase: '',
      riskLevel: 'minimal',
      measures: ''
    });

    toast.success('KI-System hinzugefügt');
  };

  const deleteSystem = (id: string) => {
    const updated = systems.filter(s => s.id !== id);
    setSystems(updated);
    localStorage.setItem('ai-systems', JSON.stringify(updated));
    toast.success('KI-System gelöscht');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            EU AI Act Risikobewertung
          </CardTitle>
          <CardDescription>
            Klassifizierung und Bewertung von KI-Systemen nach EU AI Act (Verordnung (EU) 2024/1689)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(riskLevelInfo).map(([level, info]) => {
              const Icon = info.icon;
              return (
                <div key={level} className="p-4 border rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5" />
                    <Badge variant={info.color as any}>{info.label}</Badge>
                  </div>
                  <p className="text-sm font-medium">{info.description}</p>
                  <p className="text-xs text-muted-foreground">
                    <strong>Beispiele:</strong> {info.examples}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Neues KI-System bewerten</h3>
            <div className="grid gap-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Name des KI-Systems*</Label>
                  <Input
                    value={newSystem.name}
                    onChange={(e) => setNewSystem({ ...newSystem, name: e.target.value })}
                    placeholder="z.B. EAP® Abutment-Planungs-Assistent"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Risikoklasse*</Label>
                  <Select
                    value={newSystem.riskLevel}
                    onValueChange={(value: RiskLevel) => setNewSystem({ ...newSystem, riskLevel: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minimal">Minimales Risiko</SelectItem>
                      <SelectItem value="limited">Begrenztes Risiko</SelectItem>
                      <SelectItem value="high">Hohes Risiko</SelectItem>
                      <SelectItem value="unacceptable">Unannehmbares Risiko</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Zweck der KI-Anwendung*</Label>
                <Textarea
                  value={newSystem.purpose}
                  onChange={(e) => setNewSystem({ ...newSystem, purpose: e.target.value })}
                  placeholder="Beschreiben Sie den Zweck und die Funktionsweise"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Anwendungsfall</Label>
                <Input
                  value={newSystem.useCase}
                  onChange={(e) => setNewSystem({ ...newSystem, useCase: e.target.value })}
                  placeholder="z.B. Unterstützung bei der Implantatplanung"
                />
              </div>

              <div className="space-y-2">
                <Label>Compliance-Maßnahmen</Label>
                <Textarea
                  value={newSystem.measures}
                  onChange={(e) => setNewSystem({ ...newSystem, measures: e.target.value })}
                  placeholder="Welche Maßnahmen wurden zur Einhaltung des EU AI Act getroffen?"
                  rows={3}
                />
              </div>

              <Button onClick={addSystem} className="w-full">
                <Sparkles className="mr-2 h-4 w-4" />
                KI-System hinzufügen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {systems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Erfasste KI-Systeme ({systems.length})</CardTitle>
            <CardDescription>
              Übersicht aller bewerteten KI-Systeme
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {systems.map((system) => {
                const levelInfo = riskLevelInfo[system.riskLevel];
                const Icon = levelInfo.icon;
                
                return (
                  <div key={system.id} className="p-4 border rounded-lg space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <h4 className="font-semibold text-lg">{system.name}</h4>
                        <p className="text-sm text-muted-foreground">{system.purpose}</p>
                        {system.useCase && (
                          <p className="text-sm">
                            <strong>Anwendungsfall:</strong> {system.useCase}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteSystem(system.id)}
                      >
                        Löschen
                      </Button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge variant={levelInfo.color as any} className="flex items-center gap-1">
                        <Icon className="h-3 w-3" />
                        {levelInfo.label}
                      </Badge>
                      <Badge variant="outline">
                        Bewertet: {system.assessmentDate}
                      </Badge>
                      <Badge variant={
                        system.complianceStatus === 'compliant' ? 'default' :
                        system.complianceStatus === 'non-compliant' ? 'destructive' : 'secondary'
                      }>
                        {system.complianceStatus === 'compliant' ? 'Konform' :
                         system.complianceStatus === 'non-compliant' ? 'Nicht konform' : 'In Prüfung'}
                      </Badge>
                    </div>

                    {system.measures && (
                      <div className="text-sm bg-muted/50 p-3 rounded">
                        <strong>Compliance-Maßnahmen:</strong>
                        <p className="mt-1 whitespace-pre-line">{system.measures}</p>
                      </div>
                    )}

                    {system.riskLevel === 'high' && (
                      <div className="text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/20 p-3 rounded border border-orange-200 dark:border-orange-800">
                        <strong>⚠️ Hohes Risiko:</strong> Dieses System erfordert strikte Konformitätsbewertung, 
                        Dokumentation, Risikomanagement, Datenqualität, Transparenz und menschliche Aufsicht gemäß EU AI Act.
                      </div>
                    )}

                    {system.riskLevel === 'unacceptable' && (
                      <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 p-3 rounded border border-red-200 dark:border-red-800">
                        <strong>🚫 Unannehmbares Risiko:</strong> Dieses System ist nach EU AI Act verboten und darf nicht eingesetzt werden!
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Wichtige Anforderungen nach Risikoklasse</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Hochrisiko-KI-Systeme
              </h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-6">
                <li>Risikomanagementsystem einrichten</li>
                <li>Datenqualität und -governance sicherstellen</li>
                <li>Technische Dokumentation erstellen und pflegen</li>
                <li>Transparenz und Informationspflichten gegenüber Nutzern</li>
                <li>Menschliche Aufsicht gewährleisten</li>
                <li>Robustheit, Genauigkeit und Cybersicherheit</li>
                <li>Konformitätsbewertung durchführen</li>
                <li>Erforderliche Konformitätsdokumentation vervollständigen</li>
                <li>Registrierung in EU-Datenbank</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-500" />
                Begrenzte-Risiko-KI-Systeme
              </h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-6">
                <li>Transparenzpflichten (Nutzer muss KI-Interaktion erkennen)</li>
                <li>Kennzeichnung von KI-generierten Inhalten</li>
                <li>Deepfakes müssen als solche gekennzeichnet werden</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
